import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import AddDisease from '../components/AddDisease';
import { getAllDiseases, addDisease, updateDisease } from '../lib/api';

const customStyles = `
  .dataTables_wrapper {
    padding: 0 !important;
    border: none !important;
    background: none !important;
  }
  .dataTables_wrapper .dataTables_filter,
  .dataTables_wrapper .dataTables_paginate,
  .dataTables_wrapper .dataTables_info,
  .dataTables_wrapper .dataTables_length {
    display: none !important;
  }
  .tooltip-menu {
    min-width: 140px;
    padding: 0.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background: #fff;
    z-index: 1000;
  }
  .tooltip-item {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a4a4a;
  }
  .tooltip-item:hover {
    background-color: #f1f5f9;
    color: #1a73e8;
  }
  .modal {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
    align-items: center;
    justify-content: center;
  }
  .modal.show {
    display: flex;
  }
  .modal-content {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    width: 80%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
  }
  .modal-close {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 24px;
    cursor: pointer;
  }
  .image-gallery img {
    width: 100%;
    max-height: 300px;
    object-fit: contain;
    margin: 10px 0;
  }
  .form-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='currentColor' class='bi bi-chevron-down' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 14px;
    padding-right: 20px;
    padding-left: 8px;
    height: 32px;
    line-height: 22px;
    min-width: 70px;
    max-width: 70px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    color: #333;
  }
  .navbar-search input {
    width: 24.25rem;
    height: 2.5rem;
    background-color: var(--neutral-50);
    border: 1px solid var(--input-form-light);
    border-radius: 0.5rem;
    -webkit-border-radius: 0.5rem;
    -moz-border-radius: 0.5rem;
    -ms-border-radius: 0.5rem;
    -o-border-radius: 0.5rem;
    padding-block: 0.3125rem;
    padding-inline-start: 2.625rem;
    padding-inline-end: 1.25rem;
    color: var(--text-primary-light);
  }  

  /* ===== Description enhancement (same pattern you used) ===== */
  .description-cell {
    max-width: 300px;
    min-width: 200px;
  }
  .description-text {
    display: -webkit-box;
    -webkit-line-clamp: 3;              /* collapsed: 3 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    max-height: 4.2em;                  /* 3 * 1.4 */
    word-wrap: break-word;
  }
  .description-text.expanded {
    -webkit-line-clamp: unset;          /* expanded: full text */
    max-height: none;
    overflow: visible;
    text-overflow: clip;
    white-space: pre-wrap;               /* keep any manual line breaks */
  }
  .read-more-btn {
    background: none;
    border: none;
    color: #1a73e8;
    cursor: pointer;
    font-size: 0.8rem;
    padding: 2px 0;
    margin-top: 4px;
  }
  .read-more-btn:hover {
    text-decoration: underline;
  }
`;

const DiseasesList = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [diseases, setDiseases] = useState([]);
  const [diseaseToEdit, setDiseaseToEdit] = useState(null);
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const toggleDescription = (diseaseId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [diseaseId]: !prev[diseaseId],
    }));
  };
  const needsTruncation = (description) => {
    if (!description) return false;
    return description.length > 150; // simple heuristic
  };

  const tableRef = useRef(null);

  const fetchDiseases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await getAllDiseases(token);

      const transformedDiseases = response.data.map(disease => ({
        id: disease.ID,
        CropIds: disease.CropIds,
        name: disease.DiseasesName,
        shortName: disease.DiseasesShortName,
        description: disease.Description,
        active: disease.IsActive,
        featureImage: disease.FeatureImage,
        images: disease.FeatureImage ? [disease.FeatureImage] : [],
        createdBy: disease.CreatedBy,
        createdOn: disease.CreatedOn,
        modifiedBy: disease.ModifiedBy,
        modifiedOn: disease.ModifiedOn,
        diseasesNameOtherLang: disease.DiseasesNameOtherLang,
        diseasesShortNameOtherLang: disease.DiseasesShortNameOtherLang,
        descriptionOtherLang: disease.DescriptionOtherLang
      }));


      setDiseases(transformedDiseases);
    } catch (error) {
      console.error('Failed to fetch diseases:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiseases();
  }, []);

  const toggleDrawer = (disease = null) => {
    setDiseaseToEdit(disease);
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleSubmit = async (payload, isUpdate, featureImageFile = null) => {
    try {
      const token = localStorage.getItem('token');

      const formData = new FormData();

      if (featureImageFile) {
        formData.append('FeatureImage', featureImageFile);
      }

      if (isUpdate && payload.ID) formData.append('ID', payload.ID);
      formData.append('DiseasesName', payload.DiseasesName);
      formData.append('DiseasesShortName', payload.DiseasesShortName);
      formData.append('Description', payload.Description);
      formData.append('IsActive', String(payload.IsActive));
      formData.append('CropIds', payload.CropIds);
      formData.append('DiseasesNameOtherLang', JSON.stringify(payload.DiseasesNameOtherLang));
      formData.append('DiseasesShortNameOtherLang', JSON.stringify(payload.DiseasesShortNameOtherLang));
      formData.append('DescriptionOtherLang', JSON.stringify(payload.DescriptionOtherLang));

      if (isUpdate) {
        await updateDisease(token, formData);
      } else {
        await addDisease(token, formData);
      }

      await fetchDiseases();
      setIsDrawerOpen(false);
      setDiseaseToEdit(null);
    } catch (err) {
      console.error('Failed to save disease:', err);
      throw err;
    }
  };

  const handleEdit = (disease) => {
    toggleDrawer(disease);
  };

  const filteredDiseases = diseases.filter(disease =>
    (disease.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (disease.shortName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (disease.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredDiseases.length / showEntries);
  const startIndex = (currentPage - 1) * showEntries;
  const endIndex = startIndex + showEntries;
  const currentDiseases = filteredDiseases.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showEntries]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }

    return pages;
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm"
              value={showEntries}
              onChange={(e) => setShowEntries(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>

            <div className="navbar-search">
              <div className="relative">
                <input
                  type="text"
                  className="form-control h-[36px] w-[54.25rem] pl-10 pr-3 text-sm border border-neutral-300 radius-6 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  name="search"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => toggleDrawer()}
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New Disease
          </button>
        </div>
          {loading ? (
            <div className="text-center py-4">Loading diseases...</div>
          ) : (
            <>
              <div className="table-responsive scroll-sm p-16">
                <table tableRef={tableRef} className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Disease Name</th>
                      <th scope="col">Short Name</th>
                      <th scope="col" className="description-cell">Description</th>
                      <th scope="col" className="text-center">
                        Status
                      </th>
                      <th scope="col" className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentDiseases.map((disease) => (
                      <tr key={disease.id}>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {disease.name}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {disease.shortName || '-'}
                          </span>
                        </td>
                        <td className="description-cell">
                          <div className="description-container">
                            <div
                              id={`desc-${disease.id}`}
                              className={`description-text ${expandedDescriptions[disease.id] ? 'expanded' : ''}`}
                            >
                              {disease.description || '-'}
                            </div>
                            {needsTruncation(disease.description) && (
                              <button
                                type="button"
                                className="read-more-btn"
                                onClick={() => toggleDescription(disease.id)}
                                aria-expanded={!!expandedDescriptions[disease.id]}
                                aria-controls={`desc-${disease.id}`}
                              >
                                {expandedDescriptions[disease.id] ? 'Read Less' : 'Read More'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <span
                            className={`px-24 py-4 radius-4 fw-medium text-sm ${disease.active
                                ? 'bg-success-focus text-success-600 border border-success-main'
                                : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                              }`}
                          >
                            {disease.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                            <button
                              type="button"
                              className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(disease)}
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredDiseases.length === 0 && !loading && (
                <div className="text-center py-4 text-muted">
                  No diseases found
                </div>
              )}

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16">
                <span>
                  Showing {filteredDiseases.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredDiseases.length)} of {filteredDiseases.length} entries
                </span>
                {totalPages > 1 && (
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <Icon icon="ep:d-arrow-left" className="" />
                      </button>
                    </li>

                    {getPageNumbers().map(page => (
                      <li key={page} className="page-item">
                        <button
                          className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page ? 'bg-primary-600 text-white' : ''
                            }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        <Icon icon="ep:d-arrow-right" className="" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </>
          )}
      </div>

      <AddDisease
        isOpen={isDrawerOpen}
        onClose={() => {
          setDiseaseToEdit(null);
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmit}
        diseaseToEdit={diseaseToEdit}
      />
    </>
  );
};

export default DiseasesList;
